// 📄 src/api/axiosClient.js
import axios from "axios";
import { toast } from "react-toastify";
import { requestUpgrade } from "../utils/upgradeBus"; // ← src/api → ../utils ✅
import { requestAccessDenied } from "../utils/accessDeniedBus";
import { requestBusinessSelection } from "../utils/businessSelectionBus";
import { isBusinessScopedPath } from "../utils/businessScopePaths";

// ---------------- Base URL (env overrideable) ----------------
const rawBase =
  (process.env.REACT_APP_API_BASE_URL &&
    process.env.REACT_APP_API_BASE_URL.trim()) ||
  "http://localhost:7113/api";

function normalizeBaseUrl(url) {
  const u = (url || "").replace(/\/+$/, ""); // strip trailing slashes
  return u.endsWith("/api") ? u : `${u}/api`;
}

const apiBaseUrl = normalizeBaseUrl(rawBase);

// ---------------- Token key (single source of truth) --------
export const TOKEN_KEY = "xbyte_token";
export const REFRESH_TOKEN_KEY = "xbyte_refresh_token";
export const IMPERSONATION_BACKUP_KEY = "xbyte_admin_token_backup";
export const IMPERSONATION_META_KEY = "xbyte_impersonation_meta";

// ✅ SuperAdmin selected business keys (must match AuthProvider)
const SA_BIZ_ID_KEY = "sa_selectedBusinessId";
const SA_BIZ_NAME_KEY = "sa_selectedBusinessName";

// ---------------- Axios instance ----------------------------
const axiosClient = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: false, // using Bearer tokens, not cookies
});

let authGeneration = 0;

export function getAuthGeneration() {
  return authGeneration;
}

export function beginNewAuthSession() {
  authGeneration += 1;
  return authGeneration;
}

export function invalidateAuthSession() {
  authGeneration += 1;
  refreshRequestPromise = null;
  try {
    delete axiosClient.defaults.headers.common.Authorization;
  } catch {
    // ignore header cleanup errors
  }
  return authGeneration;
}

// ------------------------------------------------------------
// ✅ JWT helper (no dependencies)
// ------------------------------------------------------------
function safeParseJwt(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    const payloadB64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");

    // base64 decode with padding
    const padded = payloadB64 + "===".slice((payloadB64.length + 3) % 4);

    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );

    return JSON.parse(json);
  } catch {
    return null;
  }
}

function extractRoleFromClaims(claims) {
  if (!claims) return null;

  // Common locations/keys across systems
  // - "role": "superadmin"
  // - "roles": ["superadmin"]
  // - Microsoft WS-Fed style role claim:
  //   "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
  const direct =
    claims.role ||
    claims.Role ||
    claims.userRole ||
    claims.UserRole ||
    claims["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] ||
    claims["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/role"];

  if (typeof direct === "string") return direct;

  const arr = claims.roles || claims.Roles;
  if (Array.isArray(arr) && arr.length > 0) return arr[0];

  return null;
}

function extractRoleKeyFromClaims(claims) {
  if (!claims) return null;

  const direct = claims.role_key || claims.roleKey || claims.RoleKey;
  if (typeof direct === "string" && direct.trim()) {
    return direct.trim().toLowerCase();
  }

  return null;
}

function isSuperAdminToken(token) {
  const claims = safeParseJwt(token);
  const roleKey = extractRoleKeyFromClaims(claims);
  if (roleKey) return roleKey === "platform.super_admin";

  const role = String(extractRoleFromClaims(claims) || "").toLowerCase();
  return role === "admin" || role === "superadmin";
}

function isImpersonationToken(token) {
  const claims = safeParseJwt(token);
  if (!claims) return false;

  const flag = claims.impersonation;
  return flag === true || String(flag || "").toLowerCase() === "true";
}

export function parseJwtClaims(token) {
  return safeParseJwt(token);
}

function restoreAdminSessionFromImpersonationExpiry() {
  try {
    const backupToken = sessionStorage.getItem(IMPERSONATION_BACKUP_KEY);
    if (!backupToken) return false;

    localStorage.setItem(TOKEN_KEY, backupToken);
    localStorage.removeItem(IMPERSONATION_META_KEY);
    localStorage.removeItem(SA_BIZ_ID_KEY);
    localStorage.removeItem(SA_BIZ_NAME_KEY);
    localStorage.removeItem("xbytechat-auth-data");
    localStorage.removeItem("businessId");
    sessionStorage.removeItem(IMPERSONATION_BACKUP_KEY);
    sessionStorage.setItem("auth_last_reason", "impersonation-expired");

    window.location.replace("/app/admin/approvals?reason=impersonation-expired");
    return true;
  } catch {
    return false;
  }
}

function getStoredRefreshToken() {
  try {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    return refreshToken && String(refreshToken).trim()
      ? String(refreshToken).trim()
      : null;
  } catch {
    return null;
  }
}

function clearStoredSessionTokens() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    // ignore storage errors
  }
}

let refreshRequestPromise = null;

export async function refreshAccessToken() {
  if (!refreshRequestPromise) {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) {
      return null;
    }

    const requestGeneration = authGeneration;
    const requestPromise = axios
      .post(
        `${apiBaseUrl}/auth/refresh-token`,
        { refreshToken },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          withCredentials: false,
        }
      )
      .then(({ data }) => {
        if (requestGeneration !== authGeneration) {
          return null;
        }

        const nextAccessToken =
          data?.accessToken ||
          data?.token ||
          data?.jwt ||
          data?.access_token ||
          null;
        const nextRefreshToken =
          data?.refreshToken || data?.refresh_token || refreshToken;

        if (!nextAccessToken) {
          throw new Error("Refresh token response did not include an access token.");
        }

        if (requestGeneration !== authGeneration) {
          return null;
        }

        localStorage.setItem(TOKEN_KEY, nextAccessToken);

        if (nextRefreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, nextRefreshToken);
        } else {
          localStorage.removeItem(REFRESH_TOKEN_KEY);
        }

        return nextAccessToken;
      })
      .catch(error => {
        if (requestGeneration === authGeneration) {
          clearStoredSessionTokens();
        }
        throw error;
      })
      .finally(() => {
        if (refreshRequestPromise === requestPromise) {
          refreshRequestPromise = null;
        }
      });

    refreshRequestPromise = requestPromise;
  }

  return refreshRequestPromise;
}

// Attach Authorization header if token exists
axiosClient.interceptors.request.use(config => {
  const token = localStorage.getItem(TOKEN_KEY);
  config.headers = config.headers || {};
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    if (typeof config.headers.delete === "function") {
      config.headers.delete("Content-Type");
      config.headers.delete("content-type");
    } else {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
    }
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (config.headers.Authorization) {
    delete config.headers.Authorization;
  }

  // Business targets are explicit per request. Never infer a superadmin target
  // from the retired global selection or overwrite a page-local admin target.

  return config;
});

// ---------------- Helpers -----------------------------------
const AUTH_PAGES = [
  "/login",
  "/signup",
  "/pending-approval",
  "/profile-completion",
];
const isOnAuthPage = () =>
  AUTH_PAGES.some(p => (window.location?.pathname || "").startsWith(p));

function isPaymentStatusReturnRoute() {
  const pathname = window.location?.pathname || "";
  if (!pathname.startsWith("/app/payment/status")) return false;

  const params = new URLSearchParams(window.location?.search || "");
  return (
    params.has("checkoutSessionId") || params.get("source") === "wallet"
  );
}

function isSuperAdminPlatformAdminRoute() {
  const pathname = window.location?.pathname || "";
  const token = localStorage.getItem(TOKEN_KEY);

  return pathname.startsWith("/app/admin") && token && isSuperAdminToken(token);
}

let showingAuthToast = false; // 401 / generic / 403 (generic)
let showingQuotaToast = false; // 429

// 403 subscription set (server-side [RequireActiveSubscription] style)
const SUBSCRIPTION_STATUS_CODES = [
  "trialexpired",
  "pastdue",
  "suspended",
  "cancelled",
  "expired",
  "noactivesubscription",
  "noplan",
  "paymentrequired",
];

function isSubscriptionAccessError(error) {
  if (!error?.response) return false;
  const { status, data } = error.response;
  if (status !== 403 || !data) return false;
  if (data.ok !== false) return false;

  const code = String(
    data.status || data.code || data.errorCode || ""
  ).toLowerCase();
  return !!code && SUBSCRIPTION_STATUS_CODES.includes(code);
}

function handleSubscriptionAccessError(error, { suppress403 }) {
  const { data } = error.response || {};
  const message =
    data?.message || "Your subscription does not allow access to this feature.";

  const path = window.location?.pathname || "";
  const onBilling =
    path.startsWith("/app/settings/billing") ||
    path.startsWith("/app/payment/status");

  if (!suppress403 && !showingAuthToast) {
    toast.error(message, { toastId: "subscription-403" });
    showingAuthToast = true;
    setTimeout(() => (showingAuthToast = false), 1500);
  }

  if (!suppress403 && !isOnAuthPage() && !onBilling) {
    setTimeout(() => {
      window.location.href = "/app/settings/billing";
    }, 800);
  }
}

// 403 feature/permission denial (upgrade flow)
const FEATURE_FORBIDDEN_CODES = [
  "featuredenied",
  "feature_denied",
  "featuredisabled",
  "feature_disabled",
  "permissiondenied",
  "permission_denied",
  "forbidden_feature",
];

function isFeatureForbidden403(error) {
  if (!error?.response) return false;
  const { status, data } = error.response;
  if (status !== 403 || !data) return false;
  if (isSubscriptionAccessError(error)) return false;

  const lower = v => String(v || "").toLowerCase();
  const code = lower(data.code || data.errorCode || data.status || data.reason);

  if (FEATURE_FORBIDDEN_CODES.includes(code)) return true;

  const msg = lower(data.message);
  if (!msg) return false;
  return (
    (msg.includes("feature") &&
      (msg.includes("denied") || msg.includes("disabled"))) ||
    (msg.includes("permission") && msg.includes("denied"))
  );
}

function handleFeatureForbidden403(error, { suppress403 }) {
  const data = error?.response?.data || {};
  const featureCode =
    data.featureCode ||
    data.permissionCode ||
    data.code ||
    data.errorCode ||
    data.reason ||
    null;

  const message =
    data?.message ||
    "This feature isn’t available on your current plan. Upgrade to continue.";

  if (!suppress403 && !showingAuthToast) {
    toast.warn(message, { toastId: "feature-403", autoClose: 4000 });
    showingAuthToast = true;
    setTimeout(() => (showingAuthToast = false), 1500);
  }

  // Fire global upgrade modal
  try {
    requestUpgrade({ reason: "feature", code: featureCode });
  } catch {
    // If the bus isn't mounted yet, fail soft
  }

  const path = window.location?.pathname || "";
  const onBilling =
    path.startsWith("/app/settings/billing") ||
    path.startsWith("/app/payment/status");

  if (!suppress403 && !isOnAuthPage() && !onBilling) {
    setTimeout(() => {
      window.location.href = "/app/settings/billing?source=feature";
    }, 800);
  }
}

const META_CATALOG_CONNECTION_ERROR_CODES = new Set([
  "META_CATALOG_PERMISSION_REQUIRED",
  "META_CATALOG_ACCESS_REVOKED",
]);

function getStructuredErrorCode(error) {
  const data = error?.response?.data;
  return String(
    data?.data?.code ||
      data?.Data?.Code ||
      data?.code ||
      data?.errorCode ||
      ""
  ).toUpperCase();
}

function isRecoverableMetaCatalog403(error) {
  return (
    error?.response?.status === 403 &&
    META_CATALOG_CONNECTION_ERROR_CODES.has(getStructuredErrorCode(error))
  );
}

function isBusinessContextUnauthorized(error) {
  if (!error?.response || error.response.status !== 401) return false;

  const lower = value => String(value || "").toLowerCase();
  const msg = lower(getErrorMessage(error));

  const hasBusinessContextMessage =
    msg.includes("business context required") ||
    msg.includes("business context missing") ||
    msg.includes("missing business context") ||
    msg.includes("please select a business") ||
    msg.includes("select a business first") ||
    msg.includes("businessid is required") ||
    msg.includes("business id is required") ||
    msg.includes("businessid missing in token") ||
    msg.includes("missing businessid in token") ||
    msg.includes("missing businessid in user claims") ||
    msg.includes("businessid in user claims") ||
    msg.includes("missing businessid") ||
    msg.includes("invalid or missing businessid in token");

  return (
    hasBusinessContextMessage ||
    isSuperAdminBusinessScoped401(error)
  );
}

function isExpiredAuthUnauthorized(error) {
  if (!error?.response || error.response.status !== 401) return false;

  const lower = value => String(value || "").toLowerCase();
  const data = error.response.data || {};
  const msg = lower(getErrorMessage(error));
  const code = lower(data.code || data.errorCode || data.status || data.reason);

  return (
    code.includes("tokenexpired") ||
    code.includes("sessionexpired") ||
    code.includes("unauthenticated") ||
    msg.includes("session expired") ||
    msg.includes("token expired") ||
    msg.includes("jwt expired") ||
    msg.includes("expired token") ||
    msg.includes("invalid token") ||
    msg.includes("signature validation") ||
    msg.includes("token validation") ||
    msg.includes("not authenticated")
  );
}

function getErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  if (data && typeof data === "object") {
    return data.message || data.error || data.title || error?.message || "";
  }
  return error?.message || "";
}

function isSuperAdminBusinessScoped401(error) {
  const token = localStorage.getItem(TOKEN_KEY);
  const pathname = window.location?.pathname || "";

  return (
    !!token &&
    isSuperAdminToken(token) &&
    isBusinessScopedPath(pathname) &&
    !isExpiredAuthUnauthorized(error)
  );
}

function handleBusinessContextUnauthorized(error, { suppress401 }) {
  const data = error?.response?.data || {};
  const message =
    getErrorMessage(error) ||
    "Business context is required. Please select a business.";

  if (
    !suppress401 &&
    !isPaymentStatusReturnRoute() &&
    !isSuperAdminPlatformAdminRoute()
  ) {
    requestBusinessSelection({
      reason: "business-context",
      message,
      code: data?.code || data?.errorCode || null,
      source: window.location.pathname,
      open: false,
    });
  }
}

function isAuthenticatedAccessDenied401(error) {
  if (!error?.response || error.response.status !== 401) return false;
  if (isBusinessContextUnauthorized(error)) return false;

  const lower = value => String(value || "").toLowerCase();
  const data = error.response.data || {};
  const msg = lower(getErrorMessage(error));
  const code = lower(data.code || data.errorCode || data.status || data.reason);

  return (
    code.includes("permission") ||
    code.includes("forbidden") ||
    code.includes("accessdenied") ||
    msg.includes("access denied") ||
    msg.includes("permission denied") ||
    msg.includes("not authorized")
  );
}

function handleAuthenticatedAccessDenied401(error, { suppress401 }) {
  const data = error?.response?.data || {};
  const message =
    data?.message ||
    "You don't have permission to access this page or perform this action.";

  if (!suppress401 && !isPaymentStatusReturnRoute()) {
    requestAccessDenied({
      reason: "permission",
      message,
      code: data?.code || data?.errorCode || data?.permissionCode || null,
    });
  }
}

// 429 traffic throttling or commercial quota denial
function isRateLimit429(error) {
  return !!error?.response && error.response.status === 429;
}

function extractRateLimitType(error) {
  const data = error?.response?.data || {};
  return String(data?.type || data?.data?.type || data?.payload?.type || "")
    .trim()
    .toLowerCase();
}

function toPositiveInt(value) {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function extractRetryAfterSeconds(error) {
  const response = error?.response || {};
  const data = response?.data || {};

  const headerRetry = Array.isArray(response?.headers?.["retry-after"])
    ? response.headers["retry-after"][0]
    : response?.headers?.["retry-after"];

  return (
    toPositiveInt(headerRetry) ??
    toPositiveInt(data?.data?.retryAfterSeconds) ??
    toPositiveInt(data?.payload?.retryAfterSeconds) ??
    toPositiveInt(data?.retryAfterSeconds)
  );
}

function extractQuotaMeta(error) {
  const data = error?.response?.data || {};
  return {
    quotaKey:
      data?.data?.quotaKey ?? data?.payload?.quotaKey ?? data?.quotaKey ?? null,
    limit: data?.data?.limit ?? data?.payload?.limit ?? data?.limit ?? null,
    remaining:
      data?.data?.remaining ??
      data?.payload?.remaining ??
      data?.remaining ??
      null,
  };
}

function isCanceledRequest(error) {
  if (!error) return false;

  if (typeof axios.isCancel === "function" && axios.isCancel(error)) {
    return true;
  }

  const code = String(error?.code || "").toUpperCase();
  const name = String(error?.name || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();

  return (
    code === "ERR_CANCELED" ||
    name === "cancelederror" ||
    name === "aborterror" ||
    message === "canceled" ||
    message === "cancelled"
  );
}

function handleRateLimit429(error, { suppress429, suppressQuotaRedirect }) {
  const data = error?.response?.data || {};
  const type = extractRateLimitType(error);
  const reason = String(data.reason || "").toUpperCase() || "QUOTA_LIMIT";
  const { quotaKey, limit, remaining } = extractQuotaMeta(error);
  const retryAfterSeconds = extractRetryAfterSeconds(error);
  const isRecognizedNonCommercialType =
    type === "temporary_rate_limit" ||
    type === "provider_rate_limit" ||
    type === "authentication_rate_limit";
  const isCommercialQuota =
    type === "plan_quota_exceeded" ||
    (!isRecognizedNonCommercialType && !!quotaKey);

  const msg = isCommercialQuota
    ? quotaKey
      ? `Quota reached: ${quotaKey}. Limit: ${
          limit ?? "n/a"
        }. Remaining: ${remaining ?? "n/a"}.`
      : data?.message || "Your plan quota has been reached."
    : retryAfterSeconds
        ? `You're sending too fast. Try again in ${retryAfterSeconds}s.`
        : "Too many requests. Please try again shortly.";

  if (!suppress429 && !showingQuotaToast) {
    toast.warn(msg, { toastId: "quota-429", autoClose: 5000 });
    showingQuotaToast = true;
    setTimeout(() => (showingQuotaToast = false), 1500);
  }

  if (!isCommercialQuota) return;

  // Trigger upgrade flow
  try {
    requestUpgrade({ reason: "quota", code: quotaKey || type || reason });
  } catch {
    // ignore
  }

  // breadcrumb for UI (e.g., Billing page can read & show details)
  try {
    sessionStorage.setItem(
      "last_quota_denial",
      JSON.stringify({
        at: new Date().toISOString(),
        reason,
        quotaKey,
        limit,
        remaining,
        path: error?.config?.url || "",
      })
    );
  } catch {}

  const path = window.location?.pathname || "";
  const onBilling =
    path.startsWith("/app/settings/billing") ||
    path.startsWith("/app/payment/status");

  if (!suppress429 && !suppressQuotaRedirect && !isOnAuthPage() && !onBilling) {
    setTimeout(() => {
      window.location.href = "/app/settings/billing?source=quota";
    }, 800);
  }
}

// ---------------- Response interceptor ----------------------
axiosClient.interceptors.response.use(
  res => res,
  async error => {
    const status = error?.response?.status;
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      "❌ Something went wrong.";

    const cfg = error?.config || {};
    const suppressToast =
      cfg.__silentToast || cfg.__silent || cfg.headers?.["x-suppress-toast"];
    const suppressGenericToast =
      suppressToast ||
      cfg.__silentGenericToast ||
      cfg.headers?.["x-suppress-generic-toast"];
    const suppress401 =
      suppressToast ||
      cfg.__silent401 ||
      cfg.headers?.["x-suppress-401-toast"] ||
      isOnAuthPage();
    const suppress403 =
      suppressToast ||
      cfg.__silent403 ||
      cfg.headers?.["x-suppress-403-toast"] ||
      isOnAuthPage();
    const suppress429 =
      suppressToast ||
      cfg.__silent429 ||
      cfg.headers?.["x-suppress-429-toast"] ||
      false;
    const suppressQuotaRedirect =
      cfg.__suppressQuotaRedirect ||
      cfg.headers?.["x-suppress-quota-redirect"] ||
      false;

    if (isCanceledRequest(error)) {
      return Promise.reject(error);
    }

    // 🔍 Detect login / signup calls so we don't redirect those 401s
    const isLoginCall =
      typeof cfg.url === "string" &&
      (cfg.url.includes("/auth/login") || cfg.url.includes("/auth/signup"));
    const isRefreshCall =
      typeof cfg.url === "string" && cfg.url.includes("/auth/refresh-token");

    // 401 → clear token, soft-redirect to login (except for /auth/login itself)
    if (status === 401) {
      // Let normal login errors bubble up to the login form
      if (isLoginCall || isRefreshCall) {
        return Promise.reject(error);
      }

      if (isBusinessContextUnauthorized(error)) {
        handleBusinessContextUnauthorized(error, { suppress401 });
        return Promise.reject(error);
      }

      if (isAuthenticatedAccessDenied401(error)) {
        handleAuthenticatedAccessDenied401(error, { suppress401 });
        return Promise.reject(error);
      }

      const currentToken = localStorage.getItem(TOKEN_KEY);
      const failureGeneration = authGeneration;
      if (
        currentToken &&
        isImpersonationToken(currentToken) &&
        restoreAdminSessionFromImpersonationExpiry()
      ) {
        return Promise.reject(error);
      }

      const canAttemptRefresh =
        !cfg.__isRetryRequest &&
        !cfg.__skipAuthRefresh &&
        !!currentToken &&
        !isImpersonationToken(currentToken) &&
        !!getStoredRefreshToken() &&
        isExpiredAuthUnauthorized(error);

      if (canAttemptRefresh) {
        try {
          const nextAccessToken = await refreshAccessToken();
          if (failureGeneration !== authGeneration) {
            return Promise.reject(error);
          }
          if (nextAccessToken) {
            return axiosClient.request({
              ...cfg,
              __isRetryRequest: true,
              headers: {
                ...(cfg.headers || {}),
                Authorization: `Bearer ${nextAccessToken}`,
              },
            });
          }
        } catch {
          // fall through to normal logout behavior
        }
      }

      if (failureGeneration !== authGeneration) {
        return Promise.reject(error);
      }

      if (localStorage.getItem(TOKEN_KEY) === currentToken) {
        clearStoredSessionTokens();
      }

      // Mark that this was a session-expired redirect
      try {
        sessionStorage.setItem("auth_last_reason", "session-expired");
      } catch {
        // ignore
      }

      if (!suppress401 && !showingAuthToast) {
        toast.error("⏰ Session expired. Please log in again.");
        showingAuthToast = true;
        setTimeout(() => (showingAuthToast = false), 2000);
      }

      if (!suppress401 && !isOnAuthPage()) {
        const redirectTo = encodeURIComponent(
          (window.location?.pathname || "") +
            (window.location?.search || "") +
            (window.location?.hash || "")
        );
        // include reason=session-expired so Login page can show a banner
        window.location.href = `/login?reason=session-expired&redirectTo=${redirectTo}`;
      }

      return Promise.reject(error);
    }

    // 403 → subscription vs feature vs generic forbidden
    if (status === 403) {
      if (isRecoverableMetaCatalog403(error)) {
        // Meta provider authorization is feature-owned recovery state, not XploreByte RBAC.
      } else if (isSubscriptionAccessError(error)) {
        handleSubscriptionAccessError(error, { suppress403 });
      } else if (isFeatureForbidden403(error)) {
        handleFeatureForbidden403(error, { suppress403 });
      } else {
        if (!suppress403 && !isPaymentStatusReturnRoute()) {
          requestAccessDenied({
            reason: "permission",
            message:
              error?.response?.data?.message ||
              "You don't have permission to access this page or perform this action.",
            code: getStructuredErrorCode(error) || null,
          });
        }
      }
      return Promise.reject(error);
    }

    // 429 → temporary/provider/auth throttle or commercial quota denial
    if (isRateLimit429(error)) {
      // Login/auth pages own authentication throttling and already suppress their
      // local request, so do not add a global CRM notification here.
      if (isLoginCall) {
        return Promise.reject(error);
      }

      handleRateLimit429(error, { suppress429, suppressQuotaRedirect });
      return Promise.reject(error);
    }

    // Generic non-401/403/429
    if (!suppressGenericToast && !showingAuthToast) {
      toast.error(msg);
      showingAuthToast = true;
      setTimeout(() => (showingAuthToast = false), 1500);
    }

    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("[Axios Error]", error);
    }

    return Promise.reject(error);
  }
);

if (process.env.NODE_ENV !== "production") {
  // eslint-disable-next-line no-console
  console.log("✅ Axios BASE URL:", axiosClient.defaults.baseURL);
}

export default axiosClient;

// // 📄 src/api/axiosClient.js
// import axios from "axios";
// import { toast } from "react-toastify";
// import { requestUpgrade } from "../utils/upgradeBus"; // ← src/api → ../utils ✅

// // ---------------- Base URL (env overrideable) ----------------
// const rawBase =
//   (process.env.REACT_APP_API_BASE_URL &&
//     process.env.REACT_APP_API_BASE_URL.trim()) ||
//   "http://localhost:7113/api";

// function normalizeBaseUrl(url) {
//   const u = (url || "").replace(/\/+$/, ""); // strip trailing slashes
//   return u.endsWith("/api") ? u : `${u}/api`;
// }

// const apiBaseUrl = normalizeBaseUrl(rawBase);

// // ---------------- Token key (single source of truth) --------
// export const TOKEN_KEY = "xbyte_token";

// // ---------------- Axios instance ----------------------------
// const axiosClient = axios.create({
//   baseURL: apiBaseUrl,
//   headers: {
//     "Content-Type": "application/json",
//     Accept: "application/json",
//   },
//   withCredentials: false, // using Bearer tokens, not cookies
// });

// // Attach Authorization header if token exists
// axiosClient.interceptors.request.use(config => {
//   const token = localStorage.getItem(TOKEN_KEY);
//   if (token) config.headers.Authorization = `Bearer ${token}`;
//   return config;
// });

// // ---------------- Helpers -----------------------------------
// const AUTH_PAGES = [
//   "/login",
//   "/signup",
//   "/pending-approval",
//   "/profile-completion",
// ];
// const isOnAuthPage = () =>
//   AUTH_PAGES.some(p => (window.location?.pathname || "").startsWith(p));

// let showingAuthToast = false; // 401 / generic / 403 (generic)
// let showingQuotaToast = false; // 429

// // 403 subscription set (server-side [RequireActiveSubscription] style)
// const SUBSCRIPTION_STATUS_CODES = [
//   "trialexpired",
//   "pastdue",
//   "suspended",
//   "cancelled",
//   "expired",
//   "noactivesubscription",
//   "noplan",
//   "paymentrequired",
// ];

// function isSubscriptionAccessError(error) {
//   if (!error?.response) return false;
//   const { status, data } = error.response;
//   if (status !== 403 || !data) return false;
//   if (data.ok !== false) return false;

//   const code = String(
//     data.status || data.code || data.errorCode || ""
//   ).toLowerCase();
//   return !!code && SUBSCRIPTION_STATUS_CODES.includes(code);
// }

// function handleSubscriptionAccessError(error, { suppress403 }) {
//   const { data } = error.response || {};
//   const message =
//     data?.message || "Your subscription does not allow access to this feature.";

//   const path = window.location?.pathname || "";
//   const onBilling =
//     path.startsWith("/app/settings/billing") ||
//     path.startsWith("/app/payment/status");

//   if (!suppress403 && !showingAuthToast) {
//     toast.error(message, { toastId: "subscription-403" });
//     showingAuthToast = true;
//     setTimeout(() => (showingAuthToast = false), 1500);
//   }

//   if (!suppress403 && !isOnAuthPage() && !onBilling) {
//     setTimeout(() => {
//       window.location.href = "/app/settings/billing";
//     }, 800);
//   }
// }

// // 403 feature/permission denial (upgrade flow)
// const FEATURE_FORBIDDEN_CODES = [
//   "featuredenied",
//   "feature_denied",
//   "featuredisabled",
//   "feature_disabled",
//   "permissiondenied",
//   "permission_denied",
//   "forbidden_feature",
// ];

// function isFeatureForbidden403(error) {
//   if (!error?.response) return false;
//   const { status, data } = error.response;
//   if (status !== 403 || !data) return false;
//   if (isSubscriptionAccessError(error)) return false;

//   const lower = v => String(v || "").toLowerCase();
//   const code = lower(data.code || data.errorCode || data.status || data.reason);

//   if (FEATURE_FORBIDDEN_CODES.includes(code)) return true;

//   const msg = lower(data.message);
//   if (!msg) return false;
//   return (
//     (msg.includes("feature") &&
//       (msg.includes("denied") || msg.includes("disabled"))) ||
//     (msg.includes("permission") && msg.includes("denied"))
//   );
// }

// function handleFeatureForbidden403(error, { suppress403 }) {
//   const data = error?.response?.data || {};
//   const featureCode =
//     data.featureCode ||
//     data.permissionCode ||
//     data.code ||
//     data.errorCode ||
//     data.reason ||
//     null;

//   const message =
//     data?.message ||
//     "This feature isn’t available on your current plan. Upgrade to continue.";

//   if (!suppress403 && !showingAuthToast) {
//     toast.warn(message, { toastId: "feature-403", autoClose: 4000 });
//     showingAuthToast = true;
//     setTimeout(() => (showingAuthToast = false), 1500);
//   }

//   // Fire global upgrade modal
//   try {
//     requestUpgrade({ reason: "feature", code: featureCode });
//   } catch {
//     // If the bus isn't mounted yet, fail soft
//   }

//   const path = window.location?.pathname || "";
//   const onBilling =
//     path.startsWith("/app/settings/billing") ||
//     path.startsWith("/app/payment/status");

//   if (!suppress403 && !isOnAuthPage() && !onBilling) {
//     setTimeout(() => {
//       window.location.href = "/app/settings/billing?source=feature";
//     }, 800);
//   }
// }

// // 429 quota/entitlement denial
// function isQuotaDenial429(error) {
//   return !!error?.response && error.response.status === 429;
// }

// function handleQuotaDenial429(error, { suppress429 }) {
//   const data = error?.response?.data || {};
//   const reason = String(data.reason || "").toUpperCase() || "QUOTA_LIMIT";
//   const quotaKey = data.quotaKey || data.key || data.code || null;

//   const msg =
//     data?.message ||
//     (quotaKey
//       ? `Limit reached for ${quotaKey}. Consider upgrading your plan.`
//       : `You're out of quota for this action. Consider upgrading your plan.`);

//   if (!suppress429 && !showingQuotaToast) {
//     toast.warn(msg, { toastId: "quota-429", autoClose: 5000 });
//     showingQuotaToast = true;
//     setTimeout(() => (showingQuotaToast = false), 1500);
//   }

//   // Trigger upgrade flow
//   try {
//     requestUpgrade({ reason: "quota", code: quotaKey || reason });
//   } catch {
//     // ignore
//   }

//   // breadcrumb for UI (e.g., Billing page can read & show details)
//   try {
//     sessionStorage.setItem(
//       "last_quota_denial",
//       JSON.stringify({
//         at: new Date().toISOString(),
//         reason,
//         quotaKey,
//         path: error?.config?.url || "",
//       })
//     );
//   } catch {}

//   const path = window.location?.pathname || "";
//   const onBilling =
//     path.startsWith("/app/settings/billing") ||
//     path.startsWith("/app/payment/status");

//   if (!suppress429 && !isOnAuthPage() && !onBilling) {
//     setTimeout(() => {
//       window.location.href = "/app/settings/billing?source=quota";
//     }, 800);
//   }
// }

// // ---------------- Response interceptor ----------------------
// axiosClient.interceptors.response.use(
//   res => res,
//   error => {
//     const status = error?.response?.status;
//     const msg =
//       error?.response?.data?.message ||
//       error?.message ||
//       "❌ Something went wrong.";

//     const cfg = error?.config || {};
//     const suppressToast =
//       cfg.__silentToast || cfg.__silent || cfg.headers?.["x-suppress-toast"];
//     const suppress401 =
//       suppressToast ||
//       cfg.__silent401 ||
//       cfg.headers?.["x-suppress-401-toast"] ||
//       isOnAuthPage();
//     const suppress403 =
//       suppressToast ||
//       cfg.__silent403 ||
//       cfg.headers?.["x-suppress-403-toast"] ||
//       isOnAuthPage();
//     const suppress429 =
//       suppressToast ||
//       cfg.__silent429 ||
//       cfg.headers?.["x-suppress-429-toast"] ||
//       false;

//     // 🔍 Detect login / signup calls so we don't redirect those 401s
//     const isLoginCall =
//       typeof cfg.url === "string" &&
//       (cfg.url.includes("/auth/login") || cfg.url.includes("/auth/signup"));

//     // 401 → clear token, soft-redirect to login (except for /auth/login itself)
//     if (status === 401) {
//       // Let normal login errors bubble up to the login form
//       if (isLoginCall) {
//         return Promise.reject(error);
//       }

//       localStorage.removeItem(TOKEN_KEY);

//       // Mark that this was a session-expired redirect
//       try {
//         sessionStorage.setItem("auth_last_reason", "session-expired");
//       } catch {
//         // ignore
//       }

//       if (!suppress401 && !showingAuthToast) {
//         toast.error("⏰ Session expired. Please log in again.");
//         showingAuthToast = true;
//         setTimeout(() => (showingAuthToast = false), 2000);
//       }

//       if (!suppress401 && !isOnAuthPage()) {
//         const redirectTo = encodeURIComponent(
//           (window.location?.pathname || "") +
//             (window.location?.search || "") +
//             (window.location?.hash || "")
//         );
//         // include reason=session-expired so Login page can show a banner
//         window.location.href = `/login?reason=session-expired&redirectTo=${redirectTo}`;
//       }

//       return Promise.reject(error);
//     }

//     // 403 → subscription vs feature vs generic forbidden
//     if (status === 403) {
//       if (isSubscriptionAccessError(error)) {
//         handleSubscriptionAccessError(error, { suppress403 });
//       } else if (isFeatureForbidden403(error)) {
//         handleFeatureForbidden403(error, { suppress403 });
//       } else {
//         if (!suppress403 && !showingAuthToast) {
//           toast.error("⛔ Access denied.");
//           showingAuthToast = true;
//           setTimeout(() => (showingAuthToast = false), 2000);
//         }
//       }
//       return Promise.reject(error);
//     }

//     // 429 → quota/entitlement denial
//     if (isQuotaDenial429(error)) {
//       handleQuotaDenial429(error, { suppress429 });
//       return Promise.reject(error);
//     }

//     // Generic non-401/403/429
//     if (!suppressToast && !showingAuthToast) {
//       toast.error(msg);
//       showingAuthToast = true;
//       setTimeout(() => (showingAuthToast = false), 1500);
//     }

//     if (process.env.NODE_ENV !== "production") {
//       // eslint-disable-next-line no-console
//       console.error("[Axios Error]", error);
//     }

//     return Promise.reject(error);
//   }
// );

// if (process.env.NODE_ENV !== "production") {
//   // eslint-disable-next-line no-console
//   console.log("✅ Axios BASE URL:", axiosClient.defaults.baseURL);
// }

// export default axiosClient;
